import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

import { 
  useReleases, 
  useDeleteRelease, 
  useReleaseLifecycle 
} from '../hooks/useRelease';
import { LoadingSpinner, EmptyState, ConfirmDialog } from '../components/common';
import { useRoles } from '../auth/useRoles';
import type { 
  ReleaseDto, 
  ReleaseLifecycleState, 
  ReleaseStatus 
} from '../types/release';

export const ReleaseListPage: React.FC = () => {
  const navigate = useNavigate();
  const { canWrite } = useRoles();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLifecycleState, setSelectedLifecycleState] = useState<ReleaseLifecycleState | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<ReleaseStatus | ''>('');
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; release?: ReleaseDto }>({
    isOpen: false,
  });
  const pageSize = 20;

  const { data, isLoading, error } = useReleases({
    nameFilter: searchTerm,
    lifecycleState: selectedLifecycleState || undefined,
    status: selectedStatus || undefined,
    page,
    pageSize,
  });

  const deleteMutation = useDeleteRelease();
  const { activateRelease, archiveRelease, markReleaseReady, isUpdating } = useReleaseLifecycle();

  const handleCreateRelease = () => {
    navigate('/releases/create');
  };

  const handleEditRelease = (release: ReleaseDto) => {
    navigate(`/releases/${release.id}/edit`);
  };

  const handleViewRelease = (release: ReleaseDto) => {
    navigate(`/releases/${release.id}`);
  };

  const handleDeleteRelease = (release: ReleaseDto) => {
    setDeleteConfirm({ isOpen: true, release });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.release) return;
    
    try {
      await deleteMutation.mutateAsync(deleteConfirm.release.id);
      toast.success('Release deleted successfully');
      setDeleteConfirm({ isOpen: false });
    } catch (error) {
      console.error('Error deleting release:', error);
      toast.error('Failed to delete release');
    }
  };

  const handleLifecycleAction = async (release: ReleaseDto, action: 'activate' | 'archive' | 'ready') => {
    try {
      const actionBy = 'current-user'; // TODO: Get from auth context
      
      switch (action) {
        case 'activate':
          await activateRelease(release.id, actionBy);
          toast.success('Release activated successfully');
          break;
        case 'archive':
          await archiveRelease(release.id, actionBy);
          toast.success('Release archived successfully');
          break;
        case 'ready':
          await markReleaseReady(release.id, actionBy);
          toast.success('Release marked as ready');
          break;
      }
    } catch (error) {
      console.error(`Error ${action} release:`, error);
      toast.error(`Failed to ${action} release`);
    }
  };

  const getLifecycleStateBadge = (state: ReleaseLifecycleState) => {
    switch (state) {
      case 'Draft':
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Draft</span>;
      case 'Ready':
        return <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Ready</span>;
      case 'Active':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Active</span>;
      case 'Archived':
        return <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Archived</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">{state}</span>;
    }
  };

  const getStatusBadge = (status: ReleaseStatus) => {
    switch (status) {
      case 'NotStarted':
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Not Started</span>;
      case 'InProgress':
        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">In Progress</span>;
      case 'Completed':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Completed</span>;
      case 'Failed':
        return <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Failed</span>;
      case 'Cancelled':
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Cancelled</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">{status}</span>;
    }
  };

  const hasFilters = searchTerm || selectedLifecycleState || selectedStatus;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Failed to load releases. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Release Management</h1>
            <p className="text-gray-600">Manage release lifecycles and pipelines</p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={handleCreateRelease}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Release
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 ml-3" />
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Search releases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <select
            className="rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={selectedLifecycleState}
            onChange={(e) => setSelectedLifecycleState(e.target.value as ReleaseLifecycleState | '')}
          >
            <option value="">All States</option>
            <option value="Draft">Draft</option>
            <option value="Ready">Ready</option>
            <option value="Active">Active</option>
            <option value="Archived">Archived</option>
          </select>

          <select
            className="rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as ReleaseStatus | '')}
          >
            <option value="">All Statuses</option>
            <option value="NotStarted">Not Started</option>
            <option value="InProgress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Failed">Failed</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setSelectedLifecycleState('');
                setSelectedStatus('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !data?.data?.length ? (
        <EmptyState
          title="No releases found"
          description={hasFilters ? "Try adjusting your search terms." : "Create your first release to get started."}
          action={hasFilters ? undefined : {
            label: "Create Release",
            onClick: handleCreateRelease,
          }}
          icon={
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.238 5.338a2.25 2.25 0 00-2.15-1.588H15M9 3.75v4.5h6V3.75M9 3.75h6" />
            </svg>
          }
        />
      ) : (
        <>
          {/* Releases Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {data.data.map((release) => (
                <li key={release.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div>
                            <p className="text-sm font-medium text-indigo-600 truncate">
                              {release.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              v{release.version} • {release.releaseFlowName}
                            </p>
                            {release.description && (
                              <p className="text-sm text-gray-500 mt-1 truncate">
                                {release.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center space-x-4">
                          {getLifecycleStateBadge(release.lifecycleState)}
                          {getStatusBadge(release.status)}
                          <span className="text-xs text-gray-500">
                            Created by {release.createdBy} • {new Date(release.createdOn).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* Lifecycle Actions */}
                        {canWrite && release.canActivate && (
                          <button
                            type="button"
                            onClick={() => handleLifecycleAction(release, 'activate')}
                            disabled={isUpdating}
                            className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                            title="Activate release"
                          >
                            <PlayIcon className="w-4 h-4" />
                          </button>
                        )}
                        
                        {canWrite && release.lifecycleState === 'Draft' && (
                          <button
                            type="button"
                            onClick={() => handleLifecycleAction(release, 'ready')}
                            disabled={isUpdating}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            title="Mark as ready"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                        
                        {canWrite && release.lifecycleState === 'Active' && (
                          <button
                            type="button"
                            onClick={() => handleLifecycleAction(release, 'archive')}
                            disabled={isUpdating}
                            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                            title="Archive release"
                          >
                            <ArchiveBoxIcon className="w-4 h-4" />
                          </button>
                        )}

                        {/* Standard Actions */}
                        <button
                          type="button"
                          onClick={() => handleViewRelease(release)}
                          className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          title="View release"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        
                        {canWrite && release.isEditable && (
                          <button
                            type="button"
                            onClick={() => handleEditRelease(release)}
                            className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
                            title="Edit release"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        
                        {canWrite && release.canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDeleteRelease(release)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete release"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Pagination */}
          {data.totalCount > pageSize && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.totalCount)} of{' '}
                {data.totalCount} releases
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  disabled={!data.hasPreviousPage}
                  onClick={() => setPage(page - 1)}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={!data.hasNextPage}
                  onClick={() => setPage(page + 1)}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false })}
        onConfirm={confirmDelete}
        title="Delete Release"
        message={`Are you sure you want to delete "${deleteConfirm.release?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        isDestructive={true}
      />
    </div>
  );
};

export default ReleaseListPage;